import { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";

const CartContext = createContext(null);

const CART_STORAGE_KEY = "novaxfit_cart";

const toNumber = (value) => {
  if (value === null || value === undefined || value === "") return null;
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : null;
};

const getItemCurrentPrice = (item) => {
  const candidates = [
    item?.price,
    item?.unitPrice,
    item?.finalPrice,
    item?.final_price,
    item?.offerPrice,
    item?.offer_price,
    item?.salePrice,
    item?.sale_price,
    item?.pvp1,
  ];

  for (const value of candidates) {
    const parsed = toNumber(value);
    if (parsed !== null) return parsed;
  }

  return 0;
};

const getItemOriginalPrice = (item, currentPrice) => {
  const candidates = [
    item?.originalPrice,
    item?.original_price,
    item?.compareAtPrice,
    item?.compare_at_price,
    item?.regularPrice,
    item?.regular_price,
    item?.basePrice,
    item?.base_price,
    item?.pvp1,
  ];

  for (const value of candidates) {
    const parsed = toNumber(value);
    if (parsed !== null && parsed > currentPrice) return parsed;
  }

  return null;
};

export function CartProvider({ children }) {
  const [cartItems, setCartItems] = useState(() => {
    try {
      const stored = localStorage.getItem(CART_STORAGE_KEY);
      return stored ? JSON.parse(stored) : [];
    } catch {
      return [];
    }
  });

  const [cartOpen, setCartOpen] = useState(false);

  useEffect(() => {
    localStorage.setItem(CART_STORAGE_KEY, JSON.stringify(cartItems));
  }, [cartItems]);

  const buildCartItemKey = useCallback((item) => {
    const productId = item?.productId ?? item?.id ?? "product";
    const variantId =
      item?.variantId ??
      item?.selectedVariant?.id ??
      item?.selectedVariantId ??
      item?.variant?.id ??
      "base";

    return `${productId}-${variantId}`;
  }, []);

  const addToCart = useCallback(
    (item) => {
      const currentPrice = getItemCurrentPrice(item);
      const originalPrice = getItemOriginalPrice(item, currentPrice);

      const normalizedItem = {
        key: buildCartItemKey(item),
        productId: item?.productId ?? item?.id ?? null,
        variantId:
          item?.variantId ??
          item?.selectedVariant?.id ??
          item?.selectedVariantId ??
          item?.variant?.id ??
          null,
        slug: item?.slug || "",
        name: item?.name || item?.title || "",
        image: item?.image || item?.thumbnail || item?.photo || "",
        quantity: Number(item?.quantity) > 0 ? Number(item.quantity) : 1,
        price: currentPrice,
        originalPrice,
        code: item?.code || item?.codigo || "",
        sku: item?.sku || "",
        variantLabel:
          item?.variantLabel ||
          item?.variant_name ||
          item?.variantName ||
          item?.selectedVariant?.name ||
          item?.selectedVariant?.value ||
          item?.variant?.name ||
          item?.variant?.value ||
          "",
      };

      setCartItems((prev) => {
        const existingIndex = prev.findIndex((cartItem) => cartItem.key === normalizedItem.key);

        if (existingIndex !== -1) {
          const updated = [...prev];
          updated[existingIndex] = {
            ...updated[existingIndex],
            slug: normalizedItem.slug || updated[existingIndex].slug,
            name: normalizedItem.name || updated[existingIndex].name,
            quantity: Number(updated[existingIndex].quantity || 0) + normalizedItem.quantity,
            price: normalizedItem.price,
            originalPrice: normalizedItem.originalPrice,
            image: normalizedItem.image || updated[existingIndex].image,
            code: normalizedItem.code || updated[existingIndex].code,
            sku: normalizedItem.sku || updated[existingIndex].sku,
            variantLabel: normalizedItem.variantLabel || updated[existingIndex].variantLabel,
          };
          return updated;
        }

        return [...prev, normalizedItem];
      });

      setCartOpen(true);
    },
    [buildCartItemKey]
  );

  const removeFromCart = useCallback((key) => {
    setCartItems((prev) => prev.filter((item) => item.key !== key));
  }, []);

  const updateCartItemQuantity = useCallback((key, quantity) => {
    const qty = Number(quantity);

    if (!Number.isFinite(qty) || qty <= 0) {
      setCartItems((prev) => prev.filter((item) => item.key !== key));
      return;
    }

    setCartItems((prev) =>
      prev.map((item) =>
        item.key === key
          ? {
              ...item,
              quantity: qty,
            }
          : item
      )
    );
  }, []);

  const increaseCartItemQuantity = useCallback((key) => {
    setCartItems((prev) =>
      prev.map((item) =>
        item.key === key
          ? {
              ...item,
              quantity: Number(item.quantity || 0) + 1,
            }
          : item
      )
    );
  }, []);

  const decreaseCartItemQuantity = useCallback((key) => {
    setCartItems((prev) =>
      prev
        .map((item) =>
          item.key === key
            ? {
                ...item,
                quantity: Number(item.quantity || 0) - 1,
              }
            : item
        )
        .filter((item) => Number(item.quantity || 0) > 0)
    );
  }, []);

  const clearCart = useCallback(() => {
    setCartItems([]);
  }, []);

  const getCartCount = useCallback(() => {
    return cartItems.reduce((sum, item) => sum + Number(item.quantity || 0), 0);
  }, [cartItems]);

  const getCartSubtotal = useCallback(() => {
    return cartItems.reduce((sum, item) => {
      const price = toNumber(item.price) ?? 0;
      const quantity = Number(item.quantity || 0);
      return sum + price * quantity;
    }, 0);
  }, [cartItems]);

  const getWhatsAppCheckoutMessage = useCallback(() => {
    if (!cartItems.length) return "";

    const origin =
      typeof window !== "undefined" && window.location?.origin
        ? window.location.origin
        : "";

    const lines = [
      "Hola, quiero realizar el siguiente pedido:",
      "",
      ...cartItems.flatMap((item, index) => {
        const qty = Number(item.quantity || 0);
        const unitPrice = toNumber(item.price) ?? 0;
        const subtotal = qty * unitPrice;
        const codeLine = item.code
          ? `Código: ${item.code}`
          : item.sku
          ? `SKU: ${item.sku}`
          : null;
        const variantLine = item.variantLabel ? `Variante: ${item.variantLabel}` : null;
        const productLink =
          item.slug && origin ? `Link: ${origin}/tienda/producto/${item.slug}` : null;

        return [
          `${index + 1}. ${item.name}`,
          codeLine,
          variantLine,
          `Cantidad: ${qty}`,
          `Precio: $${unitPrice.toFixed(2)}`,
          `Subtotal: $${subtotal.toFixed(2)}`,
          productLink,
          "",
        ].filter(Boolean);
      }),
      `Total: $${getCartSubtotal().toFixed(2)}`,
    ];

    return lines.join("\n");
  }, [cartItems, getCartSubtotal]);

  const getWhatsAppCheckoutLink = useCallback(() => {
    const message = getWhatsAppCheckoutMessage();
    return `https://wa.me/593985224301?text=${encodeURIComponent(message)}`;
  }, [getWhatsAppCheckoutMessage]);

  const value = useMemo(
    () => ({
      cartItems,
      setCartItems,
      cartOpen,
      setCartOpen,
      addToCart,
      removeFromCart,
      updateCartItemQuantity,
      increaseCartItemQuantity,
      decreaseCartItemQuantity,
      clearCart,
      getCartCount,
      getCartSubtotal,
      getWhatsAppCheckoutMessage,
      getWhatsAppCheckoutLink,
    }),
    [
      cartItems,
      cartOpen,
      addToCart,
      removeFromCart,
      updateCartItemQuantity,
      increaseCartItemQuantity,
      decreaseCartItemQuantity,
      clearCart,
      getCartCount,
      getCartSubtotal,
      getWhatsAppCheckoutMessage,
      getWhatsAppCheckoutLink,
    ]
  );

  return <CartContext.Provider value={value}>{children}</CartContext.Provider>;
}

export function useCart() {
  return useContext(CartContext);
}

export { CartContext };